import { defineStore } from 'pinia';
import _ from 'lodash';
import Vue from 'vue';
import uniqid from 'uniqid';

import TeamMemberApi from '@/store/api/system/teamMember';
import MemberTagApi from '@/extensions/restrictedArea/store/api/memberTag';
import PointOfSaleApi from '@/extensions/shop/pointOfSale/store/api/pointOfSale';
import StockApi from '@/extensions/shop/store/api/stock';

const defaultForm = {
    id: undefined,
    gender: '',
    name: '',
    first_name: '',
    last_name: '',
    email: '',
    administrators: [],
    teamUserPivot: {},
    created_at: undefined,
    updated_at: undefined,
};

const defaultRules = {
    email: 'required|email'
};

const defaultUi = {
    form: {
        loading: true,
        saving: false,
        initialized: false,
        validateOnEnter: false
    },
    teamMembersPaginated: {
        loading: true
    }
};

export const useTeamMemberStore = defineStore('teamMember', {
    state: () => ({
        teamMembersPaginated: {
            variables: {
                paginationSizes: [25, 50, 100]
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0,
                selection: {
                    type: 'inclusive',
                    identifiers: [],
                    total: 0,
                },
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            defaultSort: null,
            link: {},
            items: []
        },
        options: {
            status: {
                loading: false,
                items: [
                    { value: 'enabled', label: 'Enabled' },
                    { value: 'disabled', label: 'Disabled' }
                ]
            },
            memberTag: {
                loading: false,
                items: []
            },
            pointOfSale: {
                loading: false,
                items: []
            },
            stock: {
                loading: false,
                items: []
            },
        },
        form: _.cloneDeep(defaultForm),
        rules: _.cloneDeep(defaultRules),
        ui: _.cloneDeep(defaultUi)
    }),

    getters: {
        formInitialized: (state) => state.ui.form.initialized,
        formValidateOnEnter: (state) => state.ui.form.validateOnEnter
    },

    actions: {
        addPaginatedTeamMembersToState(data) {
            this.teamMembersPaginated.items = data.items;
            this.teamMembersPaginated.pageInfo = _.cloneDeep(data.pageInfo);
            this.teamMembersPaginated.allColumns = _.cloneDeep(data.config.allColumns);
            this.teamMembersPaginated.link = _.cloneDeep(data.config.link);
            this.teamMembersPaginated.showFilters = _.get(data.config, 'showFilters') || false;
            this.teamMembersPaginated.defaultSort = _.get(data.config, 'defaultSort') || null;
        },

        defineActiveColumns(activeColumnKeys) {
            let activeColumns = [];
            _.each(this.teamMembersPaginated.allColumns, column => {
                let allowed = _.find(activeColumnKeys, value => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.teamMembersPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.teamMembersPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.teamMembersPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (value.match === '' && value.like === '' && value.from === '' && value.to === '' && value.in.length === 0) {
                Vue.delete(this.teamMembersPaginated.filters, key);
            } else {
                Vue.set(this.teamMembersPaginated.filters, key, value);
            }
        },

        paginationBulkSelectionCheckAll({ value, indeterminate }) {
            let selection = this.teamMembersPaginated.pageInfo.selection;
            if (value === true) {
                Vue.set(selection, 'type', 'exclusive');
                Vue.set(selection, 'identifiers', []);
                Vue.set(selection, 'total', this.teamMembersPaginated.pageInfo.total);
            } else {
                Vue.set(selection, 'type', 'inclusive');
                Vue.set(selection, 'identifiers', []);
                Vue.set(selection, 'total', selection.identifiers.length);
            }
        },

        paginationBulkSelectionCheckVisible({ value, indeterminate }) {
            const items = _.get(this.teamMembersPaginated, 'items') || [];
            const ids = items.map(el => parseInt(el.id));

            let selection = this.teamMembersPaginated.pageInfo.selection;
            if (value === true) {
                Vue.set(selection, 'type', 'inclusive');

                if (!selection.identifiers.length) {
                    Vue.set(selection, 'identifiers', ids);
                } else {
                    Vue.set(selection, 'identifiers', [...selection.identifiers, ...ids]);
                }
            } else {
                Vue.set(selection, 'type', 'inclusive');
                Vue.set(selection, 'identifiers', selection.identifiers.filter(el => ids.includes(el) === false));
            }

            Vue.set(selection, 'identifiers', [...new Set(selection.identifiers)]);
            Vue.set(selection, 'total', selection.identifiers.length);
        },

        paginationBulkSelectionCheckItem(item) {
            let selection = this.teamMembersPaginated.pageInfo.selection;
            const id = parseInt(item.id);

            let checked = false;
            switch (selection.type) {
                case 'inclusive':
                    checked = !!~selection.identifiers.map(el => parseInt(el)).indexOf(id);
                    break;
                case 'exclusive':
                    checked = !~selection.identifiers.map(el => parseInt(el)).indexOf(id);
                    break;
            }

            let push = true;
            let incr = true;
            switch (selection.type) {
                case 'inclusive':
                    push = incr = checked === false;
                    break;
                case 'exclusive':
                    push = checked === true;
                    incr = checked === false;
                    break;
            }

            if (push === true) {
                const index = selection.identifiers.length;
                Vue.set(selection.identifiers, index, id);
            } else {
                const index = selection.identifiers.indexOf(id);
                if (index >= 0) Vue.delete(selection.identifiers, index);
            }

            if (incr === true) Vue.set(selection, 'total', selection.total + 1);
            else Vue.set(selection, 'total', selection.total - 1);
        },

        paginationBulkSelectionReset() {
            Vue.set(this.teamMembersPaginated.pageInfo, 'selection', {
                type: 'inclusive',
                identifiers: [],
                total: 0,
            });
        },

        definePaginationSizes(value) {
            Vue.set(this.teamMembersPaginated.variables, 'paginationSizes', value);
        },

        updatePaginationLoading(value) {
            this.ui.teamMembersPaginated.loading = value;
        },

        updateFormLoading(value) {
            this.ui.form.loading = value;
        },

        updateFormSaving(value) {
            this.ui.form.saving = value;
        },

        defineFilterVisibility(value) {
            Vue.set(this.teamMembersPaginated, 'showFilters', value);
        },

        updateRules(rules) {
            Vue.set(this, 'rules', rules);
        },

        resetRules() {
            Vue.set(this, 'rules', _.cloneDeep(defaultRules));
        },

        resetUI() {
            Vue.set(this, 'ui', _.cloneDeep(defaultUi));
        },

        insertTeamMember(teamMember) {
            Vue.set(this, 'form', teamMember);
        },

        updateMemberTagOptionsLoading(value) {
            Vue.set(this.options.memberTag, 'loading', value);
        },

        insertMemberTagOptions(tags) {
            Vue.set(this.options.memberTag, 'items', tags);
        },

        updatePointOfSaleOptionsLoading(value) {
            Vue.set(this.options.pointOfSale, 'loading', value);
        },

        insertPointOfSaleOptions(tags) {
            Vue.set(this.options.pointOfSale, 'items', tags);
        },

        updateStockOptionsLoading(value) {
            Vue.set(this.options.stock, 'loading', value);
        },

        insertStockOptions(tags) {
            Vue.set(this.options.stock, 'items', tags);
        },

        // Form UI actions
        setFormInitialized(flag) {
            Vue.set(this.ui.form, 'initialized', flag);
        },

        setFormValidateOnEnter(flag) {
            Vue.set(this.ui.form, 'validateOnEnter', flag);
        },

        // Form field actions
        setFormDisabled(value) {
            Vue.set(this.form.teamUserPivot, 'disabled', value);
        },

        setFormNotify(value) {
            Vue.set(this.form, 'notify', value);
        },

        setFormGender(value) {
            Vue.set(this.form, 'gender', value);
        },

        setFormEmail(value) {
            Vue.set(this.form, 'email', value);
        },

        setFormFirstName(value) {
            Vue.set(this.form, 'first_name', value);
            Vue.set(this.form, 'name', `${value} ${this.form.last_name}`.trim());
        },

        setFormLastName(value) {
            Vue.set(this.form, 'last_name', value);
            Vue.set(this.form, 'name', `${this.form.first_name} ${value}`.trim());
        },

        markFormAsDeleted() {
            Vue.set(this.form.teamUserPivot, 'deleted', true);
        },

        unmarkFormAsDeleted() {
            Vue.set(this.form.teamUserPivot, 'deleted', false);
        },

        updateAdministratorRight({ cms, right }) {
            let i = this.form.administrators.findIndex(el => el.cms && el.cms.id === cms.id);
            const permissionKeys = Object.keys(right.permission).filter(k => ['access', 'create', 'update', 'delete'].includes(k));

            if (i >= 0) {
                let r = this.form.administrators[i].administratorRights.findIndex(el => el.key === right.key);
                if (r >= 0) {
                    const cr = this.form.administrators[i].administratorRights[r];

                    if (permissionKeys.length && right.permission.access === undefined) {
                        if (cr.permission.access === true) {
                            delete right.permission.access;
                            delete right.permission.create;
                            delete right.permission.update;
                            delete right.permission.delete;
                        } else {
                            right.permission.access = true;
                        }
                    }
                } else {
                    if (permissionKeys.length && right.permission.access === undefined) {
                        right.permission.access = true;
                    }
                    r = this.form.administrators[i].administratorRights.length;
                }

                if (right.permission.access === undefined) right.permission.access = false;
                if (right.permission.create === undefined) right.permission.create = false;
                if (right.permission.update === undefined) right.permission.update = false;
                if (right.permission.delete === undefined) right.permission.delete = false;
                if (right.permission.any === undefined) right.permission.any = false;

                Vue.set(this.form.administrators[i].administratorRights, r, right);
            } else {
                if (permissionKeys.length && right.permission.access === undefined) {
                    right.permission.access = true;
                }

                i = this.form.administrators.length;
                const administrator = {
                    id: uniqid('new:'),
                    cms: { id: cms.id },
                    administratorRights: [right],
                };

                Vue.set(this.form.administrators, i, administrator);
            }

            let remove = true;
            for (let el of this.form.administrators[i].administratorRights) {
                if (Object.keys(el.permission).filter(k => ['access', 'create', 'update', 'delete'].includes(k) && el.permission[k] === true).length) {
                    remove = false;
                    break;
                }
            }

            if (remove) Vue.set(this.form.administrators[i], 'deleted', true);
            else if (this.form.administrators[i].deleted) Vue.delete(this.form.administrators[i], 'deleted');
        },

        // Async actions
        fetchTeamMembersPaginated(payload = {}) {
            return TeamMemberApi.fetchTeamMembersPaginated({ ...payload, ...this.teamMembersPaginated }).then(data => {
                this.addPaginatedTeamMembersToState(data);
                const activeColumnKeys = _.map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        processPaginationBulkAction(action) {
            // return TeamMemberApi.processTeamMemberPaginationBulkAction(action, this.teamMembersPaginated);
        },

        fetchTeamMember({ id, initiator }) {
            return TeamMemberApi.fetchTeamMember(id, initiator).then(teamMember => {
                this.insertTeamMember(_.cloneDeep(teamMember));
                this.resetRules();
            });
        },

        fetchMemberTagOptions(cmsID) {
            this.updateMemberTagOptionsLoading(true);
            return MemberTagApi.fetchList(cmsID)
                .then(tags => {
                    this.updateMemberTagOptionsLoading(false);
                    this.insertMemberTagOptions(tags);
                })
                .catch(err => {
                    this.updateMemberTagOptionsLoading(false);
                    throw err;
                });
        },

        fetchPointOfSaleOptions(shopID) {
            this.updatePointOfSaleOptionsLoading(true);
            return PointOfSaleApi.fetchList(shopID)
                .then(tags => {
                    this.updatePointOfSaleOptionsLoading(false);
                    this.insertPointOfSaleOptions(tags);
                })
                .catch(err => {
                    this.updatePointOfSaleOptionsLoading(false);
                    throw err;
                });
        },

        fetchStockOptions(shopID) {
            this.updateStockOptionsLoading(true);
            return StockApi.fetchList(shopID)
                .then(tags => {
                    this.updateStockOptionsLoading(false);
                    this.insertStockOptions(tags);
                })
                .catch(err => {
                    this.updateStockOptionsLoading(false);
                    throw err;
                });
        },

        validateInvitation(token) {
            return TeamMemberApi.validateInvitation(token);
        },

        acceptInvitation({ teamMember, token }) {
            return TeamMemberApi.acceptInvitation(teamMember, token);
        },

        create({ teamMember, initiator }) {
            return TeamMemberApi.create(teamMember, initiator).then((teamMember) => {
                this.insertTeamMember(_.cloneDeep(teamMember));
                this.resetRules();
            });
        },

        update({ teamMember, initiator }) {
            return TeamMemberApi.update(teamMember, initiator).then(teamMember => {
                this.insertTeamMember(_.cloneDeep(teamMember));
                this.resetRules();
            });
        },

        reset() {
            this.insertTeamMember(_.cloneDeep(defaultForm));
        }
    }
});
